// ── Forecast Tracking / Watchlist constants ────────────────────────────
// Cadence slider labels — DERIVED from scheduleMap.CADENCES (the single source
// of truth that also drives cadIdxToSpec/specToCadIdx) so the slider labels and
// the persisted schedule mapping can never drift out of index alignment.
// TRACKING_CAD = full labels; TRACKING_CAD_SHORT = compact tick labels (long
// names overflow the drawer's 330px width). The tracking cadence is stored as
// an index into these (derived from the server schedule via specToCadIdx).
import { CADENCES, cadIdxToSpec, specToCadIdx, specToEngine } from "./scheduleMap.js";
import { isVoidedContract } from "./forecast/contractStatus.js";
import { namedOutcomeIndex } from "./forecast/multiway.js";
import contractStatus from "../../../shared/contractStatus.json";
import trackingDefaults from "../../../shared/trackingDefaults.json";

// Terminal resolver STATUS TOKENS. Binary: occurred / did not occur / tie. The
// on-demand resolve route also spells a multiway outcome `resolved_multi` and a void
// `voided` (ATTEMPT_STATUS_FROM_OUTCOME_STATUS). The bus consumer spells neither:
// it persists a multiway verdict as `resolved_outcome.outcome_index` with no status,
// and a void as contract_status=voided with the verdict cleared — isConcluded reads
// those two forms separately. Deliberately WIDER than ForecastLane's
// hasTerminalVerdict (the binary triad) and report/resolutionBody's
// CONCLUSIVE_RESOLUTION_STATUSES (the triad plus `voided`) — those drive the
// verdict visual, not this gate.
const TERMINAL_RESOLUTION_STATUSES = new Set([
  "resolved_yes",
  "resolved_no",
  "resolved_tie",
  "resolved_multi",
  "voided",
]);

// A forecast is "concluded" — manual reforecasting is no longer offered — ONLY
// once it has a DEFINITIVE answer: a terminal resolver verdict (see the set
// above). Anything the user sees as "Unresolved" is NOT concluded: the
// resolver returned "unresolved", never ran, or the resolution date passed with no
// verdict (incl. a data-plane contractStatus="resolved" that never got a verdict).
// In those cases the reforecast button stays available so the user can force a
// reforecast after the fact — which also re-triggers the resolver poll, giving a
// manual path to resolution when the backend never produced one.
export function isConcluded(forecast) {
  if (!forecast) return false;
  // Read first: a void is terminal with NO verdict, so the status branches below cannot
  // see it (lib/forecast/contractStatus.js).
  if (isVoidedContract(forecast)) return true;
  if (TERMINAL_RESOLUTION_STATUSES.has(forecast.resolution?.status)) return true;
  // A bus-delivered multiway verdict names a slot and carries NO status token (slot 0 is
  // real). A slot under an explicit status defers to that status — mirrors
  // applyResolutionToDistribution, where `unresolved` wins over a stray slot.
  return !forecast.resolution?.status && namedOutcomeIndex(forecast.resolution) !== null;
}

// A RESOLVED contract that WAS tracked is kept on the watchlist so the user sees
// its OUTCOME (OCCURRED / DID NOT OCCUR), even though the server drops terminal
// contracts from the pinned map (isTracked → false). "Was tracked" == a
// non-resolution_only schedule spec survives on the row: specToCadIdx is called
// WITHOUT the contractStatus arg on purpose — passing "resolved" would force it
// to 0 (None); we want the surviving spec. `deleted` contracts are NOT kept.
// Shared by WatchlistLogContent (which rows to keep) and App's handlePin (so the
// Eye REMOVES a kept row instead of re-pinning a terminal contract).
export function isResolvedWatchlisted(f) {
  return !!f && f.contractStatus === "resolved" && specToCadIdx(f.schedule) !== 0;
}

export const TRACKING_CAD = CADENCES.map((c) => c.label);
export const TRACKING_CAD_SHORT = CADENCES.map((c) => c.short);
// 0..100 in 1% steps so the threshold slider snaps to every whole percent.
// thrIdx is the index INTO this array (so thrIdx === percent for this 1:1
// mapping). Historical note (predates this refactor): an earlier change in main
// replaced the original [5,10,15,20,30,50] preset list with this 0..100 array,
// so a prefs.thrIdx persisted before that change maps to a different threshold
// (e.g. old thrIdx=1 was 10%, now 1%); TRACKING_DEFAULTS was bumped at that time
// to keep the "10% default". Moving this constant into the module is itself
// behavior-preserving and changes none of the above.
export const TRACKING_THR = Array.from({ length: 101 }, (_, i) => i);
// Default tracking shape for a newly-pinned forecast. Server-populated
// fields (move, alert, lastRun) stay null/false until the backend job
// fires; the UI just reads whatever's there.
// cadIdx here is only a pre-load fallback; the source of truth is the
// server schedule via specToCadIdx (see getTrackingPrefs).
// `leadingBin` ships ON: a watchlisted forecast alerts when a different outcome
// takes the lead unless the user turns it off. It is a filter on an alert stream
// they already opted into (`email`), never a new channel — so defaulting it on
// can't send mail nobody asked for, and the server reads an ABSENT value the
// same way (notify/prefs.js::leadingBinEnabled), which is what makes forecasts
// already on the watchlist behave like new ones with no backfill. The value of
// `leadingBin` is shared/trackingDefaults.json (owner-map REG-2), the file the
// server reads too.
export const TRACKING_DEFAULTS = {
  cadIdx: 1,
  thrIdx: 10,
  inapp: true,
  email: false,
  leadingBin: trackingDefaults.notify.leadingBin,
};
export const TRACKING_PREFS_LS_KEY = "sooth-tracking-prefs";

// Alert prefs (email opt-in + threshold) are now persisted server-side: the
// tracking-settings controls call patchTrackingPrefs (useTrackingPrefs.js),
// which PATCHes { notify: { email, thresholdPct } } to /api/forecasts/:id and
// is read back via forecast.notify. `inapp` remains client-only (localStorage).
// The former no-op `saveAlertPrefs` stub was removed when that landed (#146).

/**
 * The alert fields a forecast's server `notify` pins, in tracking-prefs shape
 * (`thresholdPct` → `thrIdx`, 1:1). Only explicitly typed values are returned, so an
 * absent key falls through to the local cache / TRACKING_DEFAULTS — which is how an
 * absent `leadingBin` reads ON, in lockstep with the server's `leadingBinEnabled`.
 * @param {object|null|undefined} notify
 * @returns {{ email?: boolean, thrIdx?: number, leadingBin?: boolean }}
 */
export function alertPrefsFromNotify(notify) {
  const n = notify && typeof notify === "object" ? notify : {};
  const out = {};
  if (typeof n.email === "boolean") out.email = n.email;
  if (Number.isInteger(n.thresholdPct)) out.thrIdx = n.thresholdPct;
  if (typeof n.leadingBin === "boolean") out.leadingBin = n.leadingBin;
  return out;
}

/**
 * The server-owned slice of a tracking-prefs patch, in `notify` shape, or null when
 * the patch touches none of it (`inapp`/`cadIdx` are not server alert prefs).
 * @param {{ email?: boolean, thrIdx?: number, leadingBin?: boolean }} patch
 * @returns {{ email?: boolean, thresholdPct?: number, leadingBin?: boolean } | null}
 */
export function notifyPatchFromAlerts(patch) {
  const out = {};
  if (patch?.email !== undefined) out.email = patch.email;
  if (patch?.thrIdx !== undefined) out.thresholdPct = patch.thrIdx;
  if (patch?.leadingBin !== undefined) out.leadingBin = patch.leadingBin;
  return Object.keys(out).length ? out : null;
}

/**
 * Whether adding a forecast to the watchlist should turn its email alerts ON. Watching
 * means "alert me", so it does whenever the email feature is enabled — including over an
 * earlier opt-out (product decision) — and skips the write only when email is already on.
 * @param {{ notify?: object } | null | undefined} forecast
 * @param {boolean} notifyEmailEnabled
 */
export function shouldSeedWatchEmail(forecast, notifyEmailEnabled) {
  return !!notifyEmailEnabled && forecast?.notify?.email !== true;
}

const WEEKLY_CAD_IDX = CADENCES.findIndex((c) => c.label === "Weekly");
const TERMINAL_CONTRACT_STATUSES = new Set(contractStatus.terminal);

/**
 * The schedule a watch or unwatch leaves when no server decides it (sign-in off), so this browser
 * is the only watcher: the server's rule (server/handlers/watchlist.js) with
 * nobody else watching. A watch starts an unscheduled live forecast Weekly; an unwatch stops a
 * scheduled one. `null` leaves the schedule alone.
 *
 * @param {{schedule?: unknown, contractStatus?: unknown}|null|undefined} forecast
 * @param {boolean} wanted on the watchlist after this change
 * @returns {{type: string, params: object} | null}
 */
export function soleWatcherSchedule(forecast, wanted) {
  const tracked = specToCadIdx(forecast?.schedule) !== 0;
  const engine = specToEngine(forecast?.schedule);
  if (!wanted) return tracked ? cadIdxToSpec(0, engine) : null;
  if (tracked || TERMINAL_CONTRACT_STATUSES.has(forecast?.contractStatus)) return null;
  return cadIdxToSpec(WEEKLY_CAD_IDX, engine);
}
